import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Container,
  Card,
  SvgIcon,
  Button,
  Table,
  TableBody,
  TableContainer,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Radio,
  InputAdornment,
  Drawer,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

import { ReactComponent as JiraIcon } from "../../images/jira-logo.svg";
import { ReactComponent as AzureIcon } from "../../images/azure-logo.svg";
import { ReactComponent as GithubIcon } from "../../images/github-logo.svg";
import { ReactComponent as qTestIcon } from "../../images/qtest-logo.svg";
import { ReactComponent as TestrailIcon } from "../../images/testrail-logo.svg";
import { ReactComponent as SlackIcon } from "../../images/slack-logo.svg";
// import { ReactComponent as ConfluenceIcon } from "../../images/confluence-logo.svg";
import { ReactComponent as NoConnectionImage } from "../../images/NoConnectionImage.svg";

import {
  getAdminConfig,
  getAllProjects,
  getAllAdoProjects,
  getqtestAllProjects,
  getAllTestRailProjects,
  // getAllConfluenceSpaces
} from "../../services/webServices";

import ProjectsComponentPage from "./ProjectsComponentPage";

const allApps = [
  { id: "Jira", name: "Jira", icon: JiraIcon },
  { id: "Azure", name: "Azure", icon: AzureIcon },
  { id: "Github", name: "Github", icon: GithubIcon },
  { id: "qTest", name: "qTest", icon: qTestIcon },
  { id: "TestRail", name: "TestRail", icon: TestrailIcon },
  { id: "Slack", name: "Slack", icon: SlackIcon },
  // { id: "Confluence", name: "Confluence", icon: ConfluenceIcon },
];

const urlKeys = {
  Jira: "jiraUrl",
  Azure: "adoUrl",
  qTest: "qtest_url",
  TestRail: "testRailUrl",
  // Confluence: "confluenceUrl"
};

const projectAPIs = {
  Jira: getAllProjects,
  Azure: getAllAdoProjects,
  qTest: getqtestAllProjects,
  TestRail: getAllTestRailProjects,
  // Confluence: getAllConfluenceSpaces
};

const Mapping = () => {
  const [selectedApp, setSelectedApp] = useState("Jira"); // Default to Jira
  const [connections, setConnections] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedConnection, setSelectedConnection] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [hasConfigurations, setHasConfigurations] = useState(true); // Track if selected app has configurations

  // Fetch configurations for selected app
  useEffect(() => {
    async function fetchConfigs() {
      if (!selectedApp) {
        setConnections([]);
        setHasConfigurations(false);
        return;
      }

      try {
        const response = await getAdminConfig({ tool: selectedApp });
        const configurations = response.data.configurations || [];
        
        if (configurations.length === 0) {
          setConnections([]);
          setHasConfigurations(false);
        } else {
          setConnections(
            configurations.map((cfg) => {
              const url = cfg.configuration[urlKeys[selectedApp]] || "";
              const connectionName = cfg.configuration.connectionName || "";
              return {
                config_id: cfg.config_id,
                url,
                name: connectionName,
                isDefault: !!cfg.default_configuration,
                fullConfig: cfg,
              };
            })
          );
          setHasConfigurations(true);
        }
      } catch (error) {
        console.warn(`No configurations found for ${selectedApp}:`, error.message);
        setConnections([]);
        setHasConfigurations(false);
      }
    }
    
    fetchConfigs();
  }, [selectedApp]);

  const handleSelectApp = (appName) => {
    setSelectedApp(appName);
    setSelectedConnection("");
    setProjects([]);
    setSelectedProject(null);
    setError(null);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleConnectionSelect = (configId) => {
    setSelectedConnection(configId);
    setError(null);
  };

  const handleStartMapping = async () => {
    if (!selectedConnection) return;
    
    setLoadingProjects(true);
    setDrawerOpen(true);
    setSelectedProject(null);
    setError(null);
    
    try {
      const selectedConnectionData = connections.find(conn => conn.config_id === selectedConnection);
      const projectAPI = projectAPIs[selectedApp];
      
      if (!projectAPI) {
        throw new Error(`No project API found for ${selectedApp}`);
      }

      const response = await projectAPI(selectedConnectionData.fullConfig);
      
      let projectsData = response.data || [];
      
      // Handle different response formats from various tools
      if (selectedApp === 'Jira' && projectsData.data) {
        projectsData = projectsData.data;
      }
       else if (selectedApp === 'Azure' && projectsData.value) {
        projectsData = projectsData.value;
      }
      //  else if (selectedApp === 'Confluence' && projectsData.results) {
      //   projectsData = projectsData.results;
      // }
      // qTest and TestRail typically return arrays directly
      
      if (!Array.isArray(projectsData) || projectsData.length === 0) {
        setError(`No projects found in the selected ${selectedApp} connection`);
        setProjects([]);
      } else {
        setProjects(projectsData);
      }
    } catch (error) {
      console.error(`Error fetching ${selectedApp} projects:`, error);
      
      // Handle specific error cases
      if (error.response && error.response.status === 404) {
        setError(`${selectedApp} configuration not found or no projects available`);
      } else if (error.message.includes("configuration not found")) {
        setError(`${selectedApp} configuration not found. Please check your settings.`);
      } else {
        setError(`Failed to load projects from ${selectedApp}. Please try again.`);
      }
      
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleProjectSelect = (project) => {
    console.log(`Selected ${selectedApp} project:`, project);
    
    // Show success message
    setSnackbarOpen(true);
    
    // Here you would typically save the mapping or navigate to mapping page
    // For now, just show a success message and close the drawer
    setTimeout(() => {
      setDrawerOpen(false);
      setSelectedProject(null);
    }, 1000);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedProject(null);
    setError(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const filteredConnections = connections.filter(connection =>
    connection.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    connection.url.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderEmptyState = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 6,
        textAlign: "center",
        mt: 4,
      }}
    >
      <SvgIcon
        component={NoConnectionImage}
        inheritViewBox
        sx={{
          width: 120,
          height: 120,
          mb: 0,
          mr: 4,
          color: "#49A3FF",
        }}
      />
      <Typography
        variant="h6"
        sx={{
          color: "#666",
          mb: 2,
          fontWeight: "normal",
        }}
      >
        No connection established yet
      </Typography>
    </Box>
  );

  const renderNoConfigurationsState = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 6,
        textAlign: "center",
        mt: 4,
      }}
    >
      <SvgIcon
        component={NoConnectionImage}
        inheritViewBox
        sx={{
          width: 120,
          height: 120,
          mb: 2,
          color: "#49A3FF",
        }}
      />
      <Typography
        variant="h6"
        sx={{
          color: "#666",
          mb: 1,
          fontWeight: "normal",
        }}
      >
        No Configurations are set
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "#888",
        }}
      >
        Please configure {selectedApp} in Account Configuration first.
      </Typography>
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          bgcolor: "#F5F5F2",
          minHeight: "calc(100vh - 150px)",
          overflowY: "auto",
          maxHeight: "100vh",
        }}
      >
        <Container
          component="main"
          maxWidth={false}
          sx={{ marginLeft: "10px", marginRight: "10px" }}
        >
          <Box sx={{ my: 1, overflow: "hidden" }}>
            {/* App Configurations Section */}
            <Box
              sx={{
                my: 0.5,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                overflow: "hidden",
              }}
            >
              <Typography variant="h6" fontWeight="normal" fontSize="11">
                Available app configurations
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Choose any one application where your projects and tasks are
                managed.
              </Typography>
            </Box>

            {/* Show all apps - always display them */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "nowrap",
                gap: 2,
                justifyContent: "flex-start",
                bgcolor: "#fff",
                borderRadius: 2,
                p: 2,
                mt: 2,
                overflowX: "auto",
              }}
            >
              {allApps.map((app) => (
                <Box key={app.name} sx={{ textAlign: "center" }}>
                  <Card
                    variant="outlined"
                    sx={{
                      cursor: "pointer",
                      textAlign: "left",
                      height: "90px",
                      width: "125px",
                      border:
                        selectedApp === app.name
                          ? "2px solid #49A3FF"
                          : "1px solid #ddd",
                      "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "center",
                      flexDirection: "column",
                      pl: 2,
                    }}
                    onClick={() => handleSelectApp(app.name)}
                  >
                    <SvgIcon
                      sx={{ height: "35px", width: "45px" }}
                      component={app.icon}
                      inheritViewBox
                    />
                    <Typography
                      variant="h2"
                      sx={{
                        mt: 2,
                        color: "#000000",
                        fontSize: "15px",
                      }}
                    >
                      {app.name}
                    </Typography>
                  </Card>
                </Box>
              ))}
            </Box>

            <Box sx={{ mt: 4, bgcolor: "#fff", borderRadius: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, mt: 2, mr: 2 , minHeight: "60px"}}>
                <Typography variant="h6" fontWeight="normal" fontSize="11" sx={{ ml: 2 }}>
                  Available {selectedApp} Connections
                </Typography>
                {hasConfigurations && (
                  <TextField
                    placeholder="Search"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    sx={{
                      width: 350,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        height: "35px",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "#666" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </Box>

              {/* Show different states based on configuration availability */}
              {!hasConfigurations ? (
                renderNoConfigurationsState()
              ) : connections.length === 0 ? (
                renderEmptyState()
              ) : (
                <>
                  <TableContainer 
                    component={Paper} 
                    sx={{ 
                      boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)", 
                      borderRadius: 2 
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#fafafa" }}>
                          <TableCell sx={{ width: "50px", fontWeight: "600", color: "#666" }}></TableCell>
                          <TableCell sx={{ fontWeight: "600", color: "#666" }}>
                            URL ↓
                          </TableCell>
                          <TableCell sx={{ fontWeight: "600", color: "#666" }}>
                            Name ↓
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredConnections.map((connection) => (
                          <TableRow
                            key={connection.config_id}
                            sx={{
                              "&:hover": { bgcolor: "rgba(0, 0, 0, 0.02)" },
                              cursor: "pointer",
                              borderBottom: "1px solid #e0e0e0",
                            }}
                            onClick={() => handleConnectionSelect(connection.config_id)}
                          >
                            <TableCell>
                              <Radio
                                checked={selectedConnection === connection.config_id}
                                onChange={() => handleConnectionSelect(connection.config_id)}
                                sx={{
                                  color: "#ccc",
                                  "&.Mui-checked": {
                                    color: "#49A3FF",
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: "#333", fontSize: "14px" }}>
                              {connection.url}
                            </TableCell>
                            <TableCell sx={{ color: "#333", fontSize: "14px" }}>
                              {connection.name}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Start Mapping Button */}
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, pb: 3, pr: 3 }}>
                    <Button
                      variant="contained"
                      onClick={handleStartMapping}
                      sx={{
                        bgcolor: "#49A3FF",
                        color: "white",
                        textTransform: "none",
                        borderRadius: "8px",
                        px: 3,
                        py: 1,
                        fontWeight: "500",
                        "&:hover": {
                          bgcolor: "#369bff",
                        },
                        "&:disabled": {
                          bgcolor: "#ccc",
                          color: "#666",
                        },
                      }}
                      disabled={!selectedConnection}
                    >
                      Next
                    </Button>
                  </Box>
                </>
              )}
            </Box>  
          </Box>
        </Container>
      </Box>

      {/* Drawer for Projects Selection */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        sx={{
          "& .MuiDrawer-paper": {
            width: 500,
            boxSizing: "border-box",
          },
        }}
      >
        <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Box sx={{justifyContent: "flex" }}>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, textAlign: "center" }}>
              Select Project
            </Typography>
            </Box>
            <Box sx={{justifyContent: "flex-end", display: "flex", width: "50px"}}>
            <IconButton onClick={handleCloseDrawer}>
              <CloseIcon />
            </IconButton>
            </Box>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <ProjectsComponentPage
            projectsList={projects}
            selectedProject={selectedProject}
            setSelectedProject={setSelectedProject}
            projectsLoading={loadingProjects}
            taskType={selectedApp}
            onProjectSelect={handleProjectSelect}
            onClose={handleCloseDrawer}
            connectionData={connections.find(conn => conn.config_id === selectedConnection)}
          />
        </Box>
      </Drawer>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Successfully selected {selectedApp} project!
        </Alert>
      </Snackbar>
    </>
  );
};

export default Mapping;